/* Technical details drawer (2026-09-29). Pairs with snippets/product-technical-drawer.liquid.
   Open/close is handled by moncler-product-overlays.js (data-moncler-dialog-open / -close).
   This file adds: live weather marker (Open-Meteo), size/price mirroring of the page's
   product form, add-to-cart delegation, size-guide hand-off and the mobile drag handle. */
(() => {
  if (window.technicalDrawerInitialized) return;
  window.technicalDrawerInitialized = true;

  const WEATHER_KEY = 'vd_tech_weather_v1';
  const WEATHER_TTL = 30 * 60 * 1000;
  const GEO_TIMEOUT = 4000;

  const readCache = () => {
    try {
      const raw = sessionStorage.getItem(WEATHER_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || Date.now() - data.ts > WEATHER_TTL) return null;
      return data;
    } catch (_) { return null; }
  };
  const writeCache = data => { try { sessionStorage.setItem(WEATHER_KEY, JSON.stringify({ ...data, ts: Date.now() })); } catch (_) {} };

  const locate = fallback => new Promise(resolve => {
    if (!navigator.geolocation) return resolve(fallback);
    let done = false;
    const finish = value => { if (!done) { done = true; resolve(value); } };
    const timer = setTimeout(() => finish(fallback), GEO_TIMEOUT);
    navigator.geolocation.getCurrentPosition(
      pos => { clearTimeout(timer); finish({ lat: pos.coords.latitude, lon: pos.coords.longitude, here: true }); },
      () => { clearTimeout(timer); finish(fallback); },
      { timeout: GEO_TIMEOUT, maximumAge: WEATHER_TTL }
    );
  });

  const fetchTemperature = async ({ lat, lon }) => {
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat.toFixed(3)}&longitude=${lon.toFixed(3)}&current=temperature_2m`;
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) throw new Error('weather');
    const json = await response.json();
    const temp = json?.current?.temperature_2m;
    if (typeof temp !== 'number') throw new Error('weather');
    return temp;
  };

  const placeMarker = (drawer, data) => {
    const bar = drawer.querySelector('[data-tech-bar]');
    const marker = drawer.querySelector('[data-tech-marker]');
    const today = drawer.querySelector('[data-tech-today]');
    const text = drawer.querySelector('[data-tech-today-text]');
    if (!bar || !marker || !today || !text) return;
    const min = parseFloat(bar.dataset.scaleMin);
    const max = parseFloat(bar.dataset.scaleMax);
    const temp = Math.round(data.temp);
    const clamped = Math.min(max, Math.max(min, temp));
    marker.style.left = `${(((clamped - min) / (max - min)) * 100).toFixed(2)}%`;
    const template = data.here ? today.dataset.labelHere : today.dataset.labelCity;
    text.textContent = template.replace('__T__', String(temp)).replace('__C__', today.dataset.fallbackCity || '');
    marker.hidden = false;
    today.hidden = false;
  };

  const loadWeather = async drawer => {
    if (drawer.dataset.weatherLoaded) return;
    drawer.dataset.weatherLoaded = 'true';
    const today = drawer.querySelector('[data-tech-today]');
    if (!today) return;
    const cached = readCache();
    if (cached) { placeMarker(drawer, cached); return; }
    const fallback = { lat: parseFloat(today.dataset.fallbackLat), lon: parseFloat(today.dataset.fallbackLon), here: false };
    try {
      const where = await locate(fallback);
      let temp;
      try { temp = await fetchTemperature(where); }
      catch (_) { if (where.here) { where.here = false; temp = await fetchTemperature(fallback); } else throw _; }
      const data = { temp, here: !!where.here };
      writeCache(data);
      placeMarker(drawer, data);
    } catch (_) { /* marker stays hidden */ }
  };

  /* --- Product form mirroring ----------------------------------------------------
     Moncler template: hidden selects + #MonclerPrice / #MonclerAddBtn.
     Default template (data-page-section on the dialog, 2026-10-01): the
     variant-selects picker, #price-<section> and the product form's add button.
     The size there may open unselected (size-pending-option): the drawer then
     shows its placeholder and keeps the add button disabled. */
  const pageAdapter = drawer => {
    const section = drawer.dataset.pageSection;
    if (!section) {
      return {
        group: () => null,
        value: index => document.querySelector(`.moncler-hidden-select[data-option-index="${index}"]`)?.value || '',
        choose: (index, value) => {
          const option = Array.from(document.querySelectorAll(`.moncler-pdp__dropdown-option[data-index="${index}"]`)).find(el => el.dataset.value === value);
          if (option) return option.click();
          const source = document.querySelector(`.moncler-hidden-select[data-option-index="${index}"]`);
          if (source) { source.value = value; source.dispatchEvent(new Event('change')); }
        },
        price: () => document.getElementById('MonclerPrice'),
        add: () => document.getElementById('MonclerAddBtn'),
        isChangeSource: target => target.classList?.contains('moncler-hidden-select')
      };
    }
    const group = index => document.querySelector(`variant-selects[data-section="${section}"] [data-option-position="${Number(index) + 1}"]`);
    return {
      group,
      value: index => {
        const g = group(index);
        if (!g || g.hasAttribute('data-size-pending')) return '';
        const picked = g.querySelector('input[type="radio"]:checked, select option:checked');
        return picked ? picked.value : '';
      },
      choose: (index, value) => {
        const g = group(index);
        if (!g) return;
        const input = Array.from(g.querySelectorAll('input[type="radio"]')).find(el => el.value === value);
        // The page is inert behind the modal drawer, so click() may not fire change: set it and dispatch.
        if (input) { input.checked = true; input.dispatchEvent(new Event('change', { bubbles: true })); return; }
        const select = g.querySelector('select');
        if (select) { select.value = value; select.dispatchEvent(new Event('change', { bubbles: true })); }
      },
      price: () => {
        const wrap = document.getElementById(`price-${section}`);
        const price = wrap?.querySelector('.price');
        if (!price) return null;
        return price.classList.contains('price--on-sale') ? price.querySelector('.price__sale .price-item--last') : price.querySelector('.price__regular .price-item--regular');
      },
      add: () => document.querySelector(`#product-form-${section} [name="add"]`),
      isChangeSource: target => !!target.closest?.(`variant-selects[data-section="${section}"]`)
    };
  };

  const syncFromPage = drawer => {
    const page = drawer._techPage;
    const select = drawer.querySelector('[data-tech-size]');
    const valueEl = drawer.querySelector('[data-tech-size-value]');
    let sizePending = false;
    if (select) {
      const current = page.value(select.dataset.optionIndex);
      const placeholder = select.querySelector('[data-tech-size-placeholder]');
      if (current) {
        if (select.value !== current) select.value = current;
      } else if (placeholder) {
        placeholder.selected = true;
        sizePending = true;
      }
      if (valueEl) valueEl.textContent = sizePending ? (placeholder?.textContent || '') : select.value;
    }
    const price = page.price();
    const priceEl = drawer.querySelector('[data-tech-price]');
    if (price && priceEl && price.textContent.trim()) priceEl.textContent = price.textContent.trim();
    const pageAdd = page.add();
    const add = drawer.querySelector('[data-tech-add]');
    const label = drawer.querySelector('[data-tech-add-label]');
    if (pageAdd && add) {
      // A pending size disables the page button too, but it is not "sold out".
      const soldOut = pageAdd.disabled && !sizePending && !pageAdd.hasAttribute('data-size-pending');
      add.disabled = pageAdd.disabled || sizePending;
      const word = soldOut ? add.dataset.labelSoldOut : (sizePending ? (add.dataset.labelSelectSize || add.dataset.labelAdd) : add.dataset.labelAdd);
      const short = drawer.querySelector('[data-tech-add-short]');
      if (label) label.textContent = word;
      if (short) short.textContent = soldOut ? add.dataset.labelSoldOut : (sizePending ? word : add.dataset.labelAddShort);
    }
  };

  const closeDrawer = drawer => drawer.querySelector('[data-moncler-dialog-close]')?.click();

  const bindForm = drawer => {
    const page = drawer._techPage;
    const select = drawer.querySelector('[data-tech-size]');
    if (select) {
      select.addEventListener('change', () => {
        if (select.value) page.choose(select.dataset.optionIndex, select.value);
        setTimeout(() => syncFromPage(drawer), 60);
        setTimeout(() => syncFromPage(drawer), 600);
      });
    }
    drawer.querySelector('[data-tech-add]')?.addEventListener('click', () => {
      const pageAdd = page.add();
      if (!pageAdd || pageAdd.disabled) return;
      closeDrawer(drawer);
      setTimeout(() => pageAdd.click(), 120);
    });
    drawer.querySelector('[data-tech-size-guide]')?.addEventListener('click', event => {
      const id = event.currentTarget.dataset.techSizeGuide;
      const target = document.getElementById(id);
      if (!target) return;
      closeDrawer(drawer);
      setTimeout(() => {
        const opener = document.querySelector(`[data-tyre-family-compare-open="${id}"]`);
        if (opener) opener.click();
        else if (typeof target.showModal === 'function') target.showModal();
        else target.setAttribute('open', '');
      }, 380);
    });
    // Keep in step with the page's own selectors (size rows, price and stock changes).
    document.addEventListener('change', event => {
      if (page.isChangeSource(event.target)) setTimeout(() => syncFromPage(drawer), 60);
    });
    const price = page.price();
    if (price && window.MutationObserver) new MutationObserver(() => syncFromPage(drawer)).observe(price.closest('[id]') || price, { childList: true, characterData: true, subtree: true });
    const pageAdd = page.add();
    if (pageAdd && window.MutationObserver) new MutationObserver(() => syncFromPage(drawer)).observe(pageAdd, { attributes: true, attributeFilter: ['disabled', 'data-size-pending'] });
  };

  /* --- Mobile drag handle -------------------------------------------------------- */
  const bindDrag = drawer => {
    const handle = drawer.querySelector('[data-tech-handle]');
    const header = drawer.querySelector('.tech-drawer__header');
    let startY = null;
    const start = event => { if (window.innerWidth > 749) return; startY = event.touches[0].clientY; drawer.style.transition = 'none'; };
    const move = event => {
      if (startY === null) return;
      const dy = Math.max(0, event.touches[0].clientY - startY);
      drawer.style.transform = `translateY(${dy}px)`;
    };
    const end = event => {
      if (startY === null) return;
      const dy = Math.max(0, (event.changedTouches[0]?.clientY ?? startY) - startY);
      startY = null;
      drawer.style.transition = '';
      drawer.style.transform = '';
      if (dy > 90) closeDrawer(drawer);
    };
    [handle, header].forEach(el => {
      if (!el) return;
      el.addEventListener('touchstart', start, { passive: true });
      el.addEventListener('touchmove', move, { passive: true });
      el.addEventListener('touchend', end, { passive: true });
      el.addEventListener('touchcancel', end, { passive: true });
    });
  };

  const initDrawer = drawer => {
    if (drawer.dataset.techInitialized) return;
    drawer.dataset.techInitialized = 'true';
    drawer._techPage = pageAdapter(drawer);
    bindForm(drawer);
    bindDrag(drawer);
    const onOpen = () => {
      syncFromPage(drawer);
      loadWeather(drawer);
      const scroll = drawer.querySelector('[data-tech-scroll]');
      if (scroll) scroll.scrollTop = 0;
    };
    if (window.MutationObserver) {
      new MutationObserver(() => { if (drawer.open) onOpen(); }).observe(drawer, { attributes: true, attributeFilter: ['open'] });
    }
    if (drawer.open) onOpen();
  };

  const init = () => document.querySelectorAll('[data-technical-drawer]').forEach(initDrawer);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();
